import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Users } from "lucide-react";
import { useAtualizarStatusTarefa, useTarefas } from "@/hooks/useTarefas";
import {
  KANBAN_COLUNAS_TAREFAS,
  TAREFA_PRIORIDADE_COLOR,
  TAREFA_PRIORIDADE_LABEL,
  TAREFA_STATUS_COLOR,
  TAREFA_STATUS_LABEL,
  type Tarefa,
  type TarefaStatus,
} from "@/types/tarefas";
import { formatPrazoCurto, iniciais, isPrazoAtrasado } from "./tarefasUtils";

type Props = {
  apenasMinhas: boolean;
  onOpenTarefa: (tarefa: Tarefa) => void;
};

export function TarefasKanban({ apenasMinhas, onOpenTarefa }: Props) {
  const [busca, setBusca] = useState("");
  const { data: tarefas = [], isLoading } = useTarefas({
    busca: busca.trim() || null,
    apenasMinhas,
  });
  const atualizar = useAtualizarStatusTarefa();
  const [arrastandoId, setArrastandoId] = useState<string | null>(null);
  const [colunaHover, setColunaHover] = useState<TarefaStatus | null>(null);
  const [arrastou, setArrastou] = useState(false);

  const porColuna = useMemo(() => {
    const mapa = Object.fromEntries(
      KANBAN_COLUNAS_TAREFAS.map((s) => [s, [] as Tarefa[]])
    ) as Record<TarefaStatus, Tarefa[]>;
    for (const t of tarefas) mapa[t.status]?.push(t);
    return mapa;
  }, [tarefas]);

  const onDrop = (status: TarefaStatus) => {
    if (arrastandoId) {
      const t = tarefas.find((x) => x.id === arrastandoId);
      if (t && t.status !== status) {
        atualizar.mutate({ id: arrastandoId, status });
      }
    }
    setArrastandoId(null);
    setColunaHover(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fynix-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative w-full sm:w-72">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-fynix-muted" />
        <Input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Filtrar cards"
          className="pl-9 h-10 rounded-full"
        />
      </div>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {KANBAN_COLUNAS_TAREFAS.map((status) => {
            const cards = porColuna[status];
            const hover = colunaHover === status;
            return (
              <div
                key={status}
                id={`tarefas-kanban-${status}`}
                className={`w-72 shrink-0 rounded-2xl border bg-[#F3F4F6]/40 p-3 transition-colors ${
                  hover ? "border-fynix-brand ring-2 ring-fynix-brand/20" : "border-fynix-line"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setColunaHover(status);
                }}
                onDragLeave={() => setColunaHover(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  onDrop(status);
                }}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <Badge variant="outline" className={TAREFA_STATUS_COLOR[status]}>
                    {TAREFA_STATUS_LABEL[status]}
                  </Badge>
                  <span className="text-xs text-fynix-muted">{cards.length}</span>
                </div>
                <div className="space-y-2 min-h-[120px]">
                  {cards.map((t) => {
                    const atrasada = isPrazoAtrasado(t);
                    const nPart = t.participantes?.length ?? 0;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        draggable
                        onDragStart={() => {
                          setArrastandoId(t.id);
                          setArrastou(false);
                        }}
                        onDrag={() => setArrastou(true)}
                        onDragEnd={() => {
                          setArrastandoId(null);
                          setColunaHover(null);
                        }}
                        onClick={() => {
                          if (!arrastou) onOpenTarefa(t);
                        }}
                        className={`w-full text-left rounded-xl border bg-white p-3 shadow-sm hover:shadow-md transition-shadow ${
                          arrastandoId === t.id ? "opacity-50" : "border-fynix-line"
                        }`}
                      >
                        <p className="text-sm font-semibold text-fynix-ink line-clamp-2">
                          {t.titulo}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${TAREFA_PRIORIDADE_COLOR[t.prioridade]}`}
                          >
                            {TAREFA_PRIORIDADE_LABEL[t.prioridade]}
                          </Badge>
                          {t.cliente?.nome && (
                            <span className="text-[10px] text-fynix-muted truncate max-w-[120px]">
                              {t.cliente.nome}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <span
                            className={`text-[11px] ${
                              atrasada ? "text-red-600 font-semibold" : "text-fynix-muted"
                            }`}
                          >
                            {formatPrazoCurto(t.prazo)}
                          </span>
                          <div className="flex items-center gap-1">
                            {nPart > 0 && (
                              <span className="text-[10px] text-fynix-muted inline-flex items-center gap-0.5">
                                <Users className="h-3 w-3" />
                                {nPart + 1}
                              </span>
                            )}
                            <span
                              title={t.responsavel?.nome ?? ""}
                              className="h-6 w-6 rounded-full bg-fynix-brand-soft text-[10px] font-semibold text-fynix-brand flex items-center justify-center"
                            >
                              {iniciais(t.responsavel?.nome)}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
